import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  InputAdornment,
  Link,
  IconButton,
  Grid,
} from '@mui/material';
import {
  LocalOffer,
  CheckCircle,
  Close,
  LocationOn,
  CalendarToday,
  ArrowBack,
  Add,
  Remove,
  Lock,
  CreditCard,
  MailOutline,
  OpenInNew,
} from '@mui/icons-material';
import {
  API_BASE,
  palette,
  pageBackground,
  cardSx,
  textFieldSx,
  primaryButtonSx,
  outlinedButtonSx,
  assetUrl,
  formatPrice,
  fewLeftLabel,
  posterFrameSx,
  posterImgSx,
} from './ticketTheme';
import TicketTermsFooter from './TicketTermsFooter';
import TicketTermsModal from './TicketTermsModal';
import { getEventAccess, formatPublicLocation, openExternalTicketUrl } from './eventAccess';

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getSquareSdkUrl = (appId) =>
  appId?.startsWith('sandbox-')
    ? 'https://sandbox.web.squarecdn.com/v1/square.js'
    : 'https://web.squarecdn.com/v1/square.js';

const CARD_STYLE = {
  '.input-container': {
    borderColor: 'rgba(255,255,255,0.16)',
    borderRadius: '8px',
  },
  '.input-container.is-focus': {
    borderColor: '#f0b545',
  },
  '.input-container.is-error': {
    borderColor: '#ef5350',
  },
  '.message-text': {
    color: '#a2a8b3',
  },
  '.message-icon': {
    color: '#a2a8b3',
  },
  '.message-text.is-error': {
    color: '#ef5350',
  },
  '.message-icon.is-error': {
    color: '#ef5350',
  },
  input: {
    backgroundColor: '#1b1e25',
    color: '#f4f5f7',
    fontFamily: 'Arial, sans-serif',
  },
  'input::placeholder': {
    color: '#6d7380',
  },
  'input.is-error': {
    color: '#ef5350',
  },
};

let squareScriptPromise = null;
let loadedSdkUrl = null;

const loadSquareSdk = (sdkUrl) => {
  if (window.Square && loadedSdkUrl === sdkUrl) return Promise.resolve();

  if (squareScriptPromise && loadedSdkUrl === sdkUrl) return squareScriptPromise;

  squareScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${sdkUrl}"]`);
    if (existing) {
      if (window.Square) {
        loadedSdkUrl = sdkUrl;
        resolve();
      } else {
        existing.addEventListener('load', () => {
          loadedSdkUrl = sdkUrl;
          resolve();
        });
        existing.addEventListener('error', () => reject(new Error('Failed to load Square SDK')));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => {
      loadedSdkUrl = sdkUrl;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Square SDK'));
    document.head.appendChild(script);
  });

  return squareScriptPromise;
};

const penceToSquareAmount = (pence) => ((Number(pence) || 0) / 100).toFixed(2);

const buildApplePayRequestOptions = ({
  total,
  purchaseItems = [],
  chargedPlatformFee = 0,
  discountAmount = 0,
  eventTitle,
}) => {
  const lineItems = purchaseItems.map((item) => ({
    label: `${item.quantity} × ${item.typeName}`,
    amount: penceToSquareAmount(item.unitPricePence * item.quantity),
  }));

  if (chargedPlatformFee > 0) {
    lineItems.push({
      label: 'Platform fee',
      amount: penceToSquareAmount(chargedPlatformFee),
    });
  }

  const discounts = discountAmount > 0
    ? [{ label: 'Discount', amount: penceToSquareAmount(discountAmount) }]
    : undefined;

  return {
    countryCode: 'GB',
    currencyCode: 'GBP',
    requestBillingContact: false,
    requestShippingContact: false,
    lineItems,
    ...(discounts ? { discounts } : {}),
    total: {
      amount: total > 0 ? penceToSquareAmount(total) : '0.01',
      label: eventTitle || 'Tickets',
    },
  };
};

const destroyPaymentMethod = async (methodRef) => {
  if (!methodRef.current) return;
  try {
    await methodRef.current.destroy();
  } catch (destroyErr) {
    console.warn('Square payment method destroy warning:', destroyErr);
  }
  methodRef.current = null;
};

/** Numbered section label, e.g. "1 · Your details" */
const StepLabel = ({ step, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        backgroundColor: palette.accentSoft,
        border: `1px solid ${palette.accentBorder}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ color: palette.accent, fontWeight: 800, fontSize: 13 }}>{step}</Typography>
    </Box>
    <Typography sx={{ color: palette.text, fontWeight: 700, fontSize: '1rem' }}>{label}</Typography>
  </Box>
);

const SummaryRows = ({
  lineItems,
  discountAmount,
  discountNote,
  platformFee,
  chargedPlatformFee,
  platformFeeNote,
  total,
}) => (
  <>
    {lineItems.map((item) => (
      <Box key={item.ticketTypeId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ color: palette.textSecondary }}>
          {item.quantity} × {item.typeName} ({formatPrice(item.unitPricePence)})
        </Typography>
        <Typography variant="body2" sx={{ color: palette.text }}>
          {formatPrice(item.unitPricePence * item.quantity)}
        </Typography>
      </Box>
    ))}
    {platformFee > 0 && (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: palette.textSecondary }}>Platform fee</Typography>
          {chargedPlatformFee < platformFee ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: palette.textMuted, textDecoration: 'line-through' }}
              >
                {formatPrice(platformFee)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: palette.accent, fontWeight: 800 }}
              >
                {formatPrice(chargedPlatformFee)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: palette.text }}>
              {formatPrice(platformFee)}
            </Typography>
          )}
        </Box>
        {platformFeeNote && (
          <Typography variant="caption" sx={{ color: palette.textMuted, display: 'block', mt: 0.25 }}>
            {platformFeeNote}
          </Typography>
        )}
      </Box>
    )}
    {discountAmount > 0 && (
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" sx={{ color: palette.success }}>Discount</Typography>
          <Typography variant="body2" sx={{ color: palette.success }}>-{formatPrice(discountAmount)}</Typography>
        </Box>
        {discountNote && (
          <Typography variant="caption" sx={{ color: palette.success, display: 'block', mt: 0.25 }}>
            {discountNote}
          </Typography>
        )}
      </Box>
    )}
    <Divider sx={{ borderColor: palette.border, my: 1.25 }} />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Typography sx={{ color: palette.text, fontWeight: 700 }}>Total</Typography>
      <Typography sx={{ color: palette.accent, fontWeight: 800, fontSize: '1.35rem' }}>
        {formatPrice(total)}
      </Typography>
    </Box>
  </>
);

const TicketPurchase = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [quantities, setQuantities] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [loading, setLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardReady, setCardReady] = useState(false);
  const [cardInitError, setCardInitError] = useState('');
  const [cardInitKey, setCardInitKey] = useState(0);
  const [squareConfig, setSquareConfig] = useState(null);
  const [applePayAvailable, setApplePayAvailable] = useState(false);
  const [googlePayAvailable, setGooglePayAvailable] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const cardRef = useRef(null);
  const applePayRef = useRef(null);
  const googlePayRef = useRef(null);
  const paymentRequestRef = useRef(null);
  const checkoutSnapshotRef = useRef({
    total: 0,
    purchaseItems: [],
    chargedPlatformFee: 0,
    discountAmount: 0,
    eventTitle: 'Tickets',
  });
  const cardContainerRef = useRef(null);
  const initInProgressRef = useRef(false);

  const waitForCardContainer = (maxAttempts = 30, intervalMs = 100) =>
    new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        const container = cardContainerRef.current;
        if (container) {
          resolve(container);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          reject(new Error('Payment form container not found'));
          return;
        }
        setTimeout(check, intervalMs);
      };
      check();
    });

  useEffect(() => {
    if (!eventId) {
      navigate('/tickets');
      return;
    }
    fetchEvent();
    fetchSquareConfig();
  }, [eventId, navigate]);

  const fetchEvent = async () => {
    setEventLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tickets/events/${eventId}`);
      const data = await res.json();
      if (data.success) {
        setEvent(data.event);
        const initial = {};
        (data.event.ticket_types || []).forEach((type) => {
          initial[type.id] = 0;
        });
        setQuantities(initial);
      } else {
        navigate('/tickets');
      }
    } catch (e) {
      console.error('Failed to fetch event', e);
      navigate('/tickets');
    } finally {
      setEventLoading(false);
    }
  };

  const fetchSquareConfig = async () => {
    const envAppId = process.env.REACT_APP_SQUARE_APP_ID;
    const envLocationId = process.env.REACT_APP_SQUARE_LOCATION_ID;

    if (envAppId && envLocationId) {
      setSquareConfig({ applicationId: envAppId, locationId: envLocationId });
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/tickets/square-config`);
      const data = await res.json();
      if (data.applicationId && data.locationId) {
        setSquareConfig(data);
      }
    } catch (e) {
      console.error('Failed to fetch Square config', e);
    }
  };

  useEffect(() => {
    if (!squareConfig || eventLoading || !event) return;
    if (!getEventAccess(event).isInternalTickets || event.is_sold_out) return;

    let cancelled = false;

    const initializeCard = async () => {
      if (initInProgressRef.current) return;
      initInProgressRef.current = true;
      setCardInitError('');
      setCardReady(false);
      setApplePayAvailable(false);
      setGooglePayAvailable(false);

      const { applicationId, locationId } = squareConfig;
      if (!applicationId || !locationId) {
        setCardInitError('Square payment is not configured. Missing application or location ID.');
        initInProgressRef.current = false;
        return;
      }

      try {
        const sdkUrl = getSquareSdkUrl(applicationId);
        await loadSquareSdk(sdkUrl);
        if (cancelled) return;

        const container = await waitForCardContainer();
        if (cancelled) return;

        await destroyPaymentMethod(cardRef);
        await destroyPaymentMethod(applePayRef);
        await destroyPaymentMethod(googlePayRef);
        paymentRequestRef.current = null;

        container.innerHTML = '';

        const payments = window.Square.payments(applicationId, locationId);

        try {
          const card = await payments.card({ style: CARD_STYLE });
          await card.attach(container);
          if (cancelled) {
            await card.destroy();
            return;
          }
          cardRef.current = card;
          setCardReady(true);
        } catch (cardErr) {
          console.error('Square card init error:', cardErr);
          if (!cancelled) {
            setCardInitError(
              cardErr?.message || 'Failed to initialize payment form. Please try again.'
            );
          }
        }

        // Shared payment request for wallet payment methods (Apple Pay & Google Pay)
        const paymentRequest = payments.paymentRequest(
          buildApplePayRequestOptions(checkoutSnapshotRef.current)
        );
        paymentRequestRef.current = paymentRequest;

        try {
          const applePay = await payments.applePay(paymentRequest);
          if (cancelled) {
            await applePay.destroy();
            return;
          }
          applePayRef.current = applePay;
          setApplePayAvailable(true);
        } catch (applePayErr) {
          console.info('Apple Pay not available:', applePayErr?.message || applePayErr);
          applePayRef.current = null;
          if (!cancelled) setApplePayAvailable(false);
        }

        try {
          const googlePay = await payments.googlePay(paymentRequest);
          await googlePay.attach('#google-pay-button');
          if (cancelled) {
            await googlePay.destroy();
            return;
          }
          googlePayRef.current = googlePay;
          if (!cancelled) setGooglePayAvailable(true);
        } catch (googlePayErr) {
          console.info('Google Pay not available:', googlePayErr?.message || googlePayErr);
          googlePayRef.current = null;
          if (!cancelled) setGooglePayAvailable(false);
        }
      } catch (e) {
        console.error('Square card init error:', e);
        if (!cancelled) {
          setCardInitError(
            e?.message || 'Failed to initialize payment form. Please try again.'
          );
        }
      } finally {
        initInProgressRef.current = false;
      }
    };

    const timer = setTimeout(initializeCard, 50);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      destroyPaymentMethod(cardRef);
      destroyPaymentMethod(applePayRef);
      destroyPaymentMethod(googlePayRef);
      paymentRequestRef.current = null;
      initInProgressRef.current = false;
    };
  }, [squareConfig, eventLoading, event, cardInitKey]);

  useEffect(() => {
    if ((!applePayAvailable && !googlePayAvailable) || !paymentRequestRef.current) return;
    try {
      paymentRequestRef.current.update(
        buildApplePayRequestOptions(checkoutSnapshotRef.current)
      );
    } catch (e) {
      console.warn('Payment request update failed:', e);
    }
  }, [applePayAvailable, googlePayAvailable, quantities, couponApplied, event]);

  const ticketTypes = (event?.ticket_types || []).filter((t) => t.active !== 0 && t.active !== false);

  const purchaseItems = ticketTypes
    .filter((type) => (quantities[type.id] || 0) > 0)
    .map((type) => ({
      ticketTypeId: type.id,
      typeName: type.name,
      quantity: quantities[type.id],
      unitPricePence: type.price_pence,
    }));

  const totalQuantity = purchaseItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = purchaseItems.reduce(
    (sum, item) => sum + item.unitPricePence * item.quantity,
    0
  );
  const isPerTicketFee = event?.platform_fee_type === 'per_ticket';
  const rawPlatformFee = (event?.platform_fee_enabled && totalQuantity)
    ? (isPerTicketFee
      ? (event.platform_fee_pence || 0) * totalQuantity
      : (event.platform_fee_pence || 0))
    : 0;
  const discountAmount = couponApplied ? couponApplied.discountAmount : 0;
  const platformFee = couponApplied ? (couponApplied.platformFee || 0) : rawPlatformFee;
  const chargedPlatformFee = couponApplied
    ? (couponApplied.chargedPlatformFee ?? (couponApplied.waivePlatformFee ? 0 : platformFee))
    : rawPlatformFee;
  const total = couponApplied
    ? couponApplied.finalAmount
    : Math.max(0, subtotal - discountAmount + chargedPlatformFee);
  const isFreeOrder = purchaseItems.length > 0 && total <= 0;
  const savingsAmount = discountAmount + Math.max(0, platformFee - chargedPlatformFee);
  const discountNote = couponApplied
    && couponApplied.discountedTicketCount > 0
    && couponApplied.discountedTicketCount < totalQuantity
    ? (
      couponApplied.applicableTicketTypes?.length
        ? `Applied to ${couponApplied.discountedTicketCount} of ${totalQuantity} tickets (${couponApplied.applicableTicketTypes.join(', ')})`
        : `Applied to ${couponApplied.discountedTicketCount} of ${totalQuantity} tickets`
    )
    : (couponApplied?.applicableTicketTypes?.length
      ? `Applies to ${couponApplied.applicableTicketTypes.join(', ')} tickets`
      : '');
  const platformFeeNote = couponApplied
    && isPerTicketFee
    && couponApplied.platformFeeTicketsWaived > 0
    && couponApplied.platformFeeTicketsWaived < totalQuantity
    ? `Fee waived on ${couponApplied.platformFeeTicketsWaived} of ${totalQuantity} tickets`
    : (event?.platform_fee_enabled && isPerTicketFee && totalQuantity
      ? `${formatPrice(event.platform_fee_pence)} per ticket`
      : '');

  checkoutSnapshotRef.current = {
    total,
    purchaseItems,
    chargedPlatformFee,
    discountAmount,
    eventTitle: event?.title || 'Tickets',
  };

  const getMaxQtyForType = (type) => {
    const typeRemaining = type.tickets_remaining != null
      ? type.tickets_remaining
      : 10;
    const eventRemaining = event?.tickets_remaining != null
      ? event.tickets_remaining
      : 10;
    const otherQty = totalQuantity - (quantities[type.id] || 0);
    const eventCap = Math.max(0, eventRemaining - otherQty);
    return Math.min(10 - otherQty, typeRemaining, eventCap || 10);
  };

  const changeTypeQuantity = (typeId, delta) => {
    setQuantities((prev) => {
      const type = ticketTypes.find((t) => t.id === typeId);
      if (!type) return prev;
      const current = prev[typeId] || 0;
      const otherQty = Object.entries(prev).reduce(
        (sum, [id, qty]) => (Number(id) === Number(typeId) ? sum : sum + (qty || 0)),
        0
      );
      const typeRemaining = type.tickets_remaining != null ? type.tickets_remaining : 10;
      const eventRemaining = event?.tickets_remaining != null ? event.tickets_remaining : 10;
      const eventCap = Math.max(0, eventRemaining - otherQty);
      const maxQty = Math.min(10 - otherQty, typeRemaining, eventCap || 10);
      const next = Math.max(0, Math.min(maxQty, current + delta));
      return { ...prev, [typeId]: next };
    });
    setCouponApplied(null);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!purchaseItems.length) {
      setCouponError('Select tickets before applying a discount code');
      return;
    }
    setCouponError('');
    setCouponLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tickets/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: couponCode.trim(),
          eventId,
          items: purchaseItems.map(({ ticketTypeId, quantity }) => ({ ticketTypeId, quantity })),
        }),
      });
      const data = await res.json();
      if (data.valid) {
        setCouponApplied(data);
      } else {
        setCouponError(data.message || 'Invalid coupon');
        setCouponApplied(null);
      }
    } catch (e) {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
  };

  const validateCheckoutFields = () => {
    if (!name.trim() || !email.trim()) {
      setError('Please fill in your name and email');
      return false;
    }
    if (!purchaseItems.length) {
      setError('Please select at least one ticket');
      return false;
    }
    return true;
  };

  const submitPurchase = async (sourceId) => {
    const res = await fetch(`${API_BASE}/api/tickets/purchase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name.trim(),
        email: email.trim(),
        eventId,
        items: purchaseItems.map(({ ticketTypeId, quantity }) => ({ ticketTypeId, quantity })),
        sourceId,
        couponCode: couponApplied ? couponCode.trim() : undefined,
      }),
    });

    const data = await res.json();
    if (data.success) {
      const params = new URLSearchParams({
        ticketId: data.ticketId,
        email: data.email || email.trim(),
      });
      navigate(`/tickets/success?${params.toString()}`, {
        state: {
          verificationCode: data.verificationCode,
          eventName: data.eventName,
          entries: data.entries || [],
          quantity: totalQuantity,
        },
      });
      return true;
    }

    setError(data.message || 'Purchase failed');
    return false;
  };

  const handlePurchase = async () => {
    setError('');
    if (!validateCheckoutFields()) return;

    if (!isFreeOrder && !cardRef.current) {
      setError('Payment form not ready. Please refresh.');
      return;
    }

    setLoading(true);
    try {
      let sourceId;
      if (!isFreeOrder) {
        const result = await cardRef.current.tokenize();
        if (result.status !== 'OK') {
          setError('Card verification failed. Please check your details.');
          return;
        }
        sourceId = result.token;
      }
      await submitPurchase(sourceId);
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplePay = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateCheckoutFields()) return;
    if (isFreeOrder || !applePayRef.current) return;

    // Keep the Apple Pay sheet amount in sync, then tokenize immediately
    // in this click handler (Apple requires no awaits before tokenize).
    try {
      paymentRequestRef.current?.update?.(
        buildApplePayRequestOptions(checkoutSnapshotRef.current)
      );
    } catch (updateErr) {
      console.warn('Apple Pay payment request update failed:', updateErr);
    }

    try {
      const result = await applePayRef.current.tokenize();
      if (result.status === 'Cancel') return;
      if (result.status !== 'OK' || !result.token) {
        setError('Apple Pay was not completed. You can try again or pay with card.');
        return;
      }
      setLoading(true);
      await submitPurchase(result.token);
    } catch (err) {
      const message = err?.message || '';
      if (/cancel/i.test(message)) return;
      setError('Apple Pay failed. Please try again or pay with card.');
    } finally {
      setLoading(false);
    }
  };

  const handleGooglePay = async () => {
    setError('');
    if (!validateCheckoutFields()) return;
    if (isFreeOrder || !googlePayRef.current) return;

    try {
      paymentRequestRef.current?.update?.(
        buildApplePayRequestOptions(checkoutSnapshotRef.current)
      );
    } catch (updateErr) {
      console.warn('Google Pay payment request update failed:', updateErr);
    }

    setLoading(true);
    try {
      const result = await googlePayRef.current.tokenize();
      if (result.status === 'Cancel') {
        setLoading(false);
        return;
      }
      if (result.status !== 'OK' || !result.token) {
        setError('Google Pay was not completed. You can try again or pay with card.');
        setLoading(false);
        return;
      }
      await submitPurchase(result.token);
    } catch (err) {
      const message = err?.message || '';
      if (/cancel/i.test(message)) {
        setLoading(false);
        return;
      }
      setError('Google Pay failed. Please try again or pay with card.');
    } finally {
      setLoading(false);
    }
  };

  if (eventLoading) {
    return (
      <Box sx={{ ...pageBackground, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: palette.accent }} />
      </Box>
    );
  }

  const soldOut = event?.is_sold_out;
  const fewLeft = event?.is_few_left;
  const access = getEventAccess(event);
  const locationLabel = formatPublicLocation(event, access);
  const showInternalCheckout = access.isInternalTickets && !soldOut;

  const summaryProps = {
    lineItems: purchaseItems,
    discountAmount,
    discountNote,
    platformFee,
    chargedPlatformFee,
    platformFeeNote,
    total,
  };

  return (
    <Box sx={{ ...pageBackground, py: { xs: 2, md: 5 }, px: 2 }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Link
          component={RouterLink}
          to="/tickets"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: palette.textSecondary,
            mb: 2,
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 600,
            '&:hover': { color: palette.accent },
          }}
        >
          <ArrowBack sx={{ fontSize: 17, mr: 0.5 }} /> All Events
        </Link>

        <Grid container spacing={{ xs: 2.5, md: 4 }}>
          {/* ============ LEFT — event (1/3 on desktop) ============ */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
              <Paper elevation={0} sx={{ ...cardSx }}>
                {event?.image_url ? (
                  <Box sx={posterFrameSx}>
                    <Box
                      component="img"
                      src={assetUrl(event.image_url)}
                      alt={event.title}
                      sx={posterImgSx}
                    />
                  </Box>
                ) : null}
                <Box sx={{ p: 2.5 }}>
                  {(access.isPrivate || access.isFreeEntry || (access.isInternalTickets && (soldOut || fewLeft))) && (
                    <Chip
                      size="small"
                      icon={access.isPrivate ? <Lock sx={{ fontSize: 14 }} /> : undefined}
                      label={
                        access.isPrivate
                          ? 'Private Event'
                          : access.isFreeEntry
                            ? 'FREE ENTRY'
                            : (soldOut ? 'Sold Out' : fewLeftLabel(event))
                      }
                      sx={{
                        mb: 1.5,
                        fontWeight: 700,
                        backgroundColor: access.isPrivate
                          ? 'rgba(255,255,255,0.08)'
                          : access.isFreeEntry
                            ? palette.successSoft
                            : soldOut ? 'rgba(239,83,80,0.15)' : 'rgba(255,167,38,0.15)',
                        color: access.isPrivate
                          ? palette.text
                          : access.isFreeEntry
                            ? palette.success
                            : soldOut ? palette.error : palette.warning,
                        border: `1px solid ${
                          access.isPrivate
                            ? palette.borderStrong
                            : access.isFreeEntry
                              ? palette.successBorder
                              : soldOut ? 'rgba(239,83,80,0.4)' : 'rgba(255,167,38,0.4)'
                        }`,
                      }}
                    />
                  )}
                  <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800, lineHeight: 1.3 }}>
                    {event?.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: palette.accent, fontWeight: 600, mt: 0.25, mb: 1.5 }}>
                    {event?.event_name}
                  </Typography>

                  {event?.event_date && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <CalendarToday sx={{ fontSize: 16, color: palette.textMuted, mt: 0.25 }} />
                      <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                        {formatDate(event.event_date)}
                      </Typography>
                    </Box>
                  )}
                  {locationLabel && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <LocationOn sx={{ fontSize: 16, color: palette.textMuted, mt: 0.25 }} />
                      {!access.isPrivate && event.venue_location_link ? (
                        <Link
                          href={event.venue_location_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: palette.textSecondary, fontSize: 14, textDecoration: 'underline', textDecorationColor: palette.borderStrong, '&:hover': { color: palette.accent } }}
                        >
                          {locationLabel}
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ color: palette.textSecondary }}>{locationLabel}</Typography>
                      )}
                    </Box>
                  )}
                  {event?.description && !access.isPrivate && (
                    <Typography variant="body2" sx={{ color: palette.textMuted, mt: 1.5 }}>
                      {event.description}
                    </Typography>
                  )}
                </Box>

                {/* Order summary — desktop sidebar */}
                {showInternalCheckout && (
                  <Box sx={{ display: { xs: 'none', md: 'block' }, borderTop: `1px dashed ${palette.borderStrong}`, p: 2.5 }}>
                    <Typography variant="overline" sx={{ color: palette.textMuted, fontWeight: 700, letterSpacing: 1.5 }}>
                      Order Summary
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <SummaryRows {...summaryProps} />
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>
          </Grid>

          {/* ============ RIGHT — checkout (2/3 on desktop) ============ */}
          <Grid item xs={12} md={8}>
            {access.isPrivate ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                <Lock sx={{ fontSize: 36, color: palette.textMuted, mb: 1.5 }} />
                <Typography variant="h6" sx={{ color: palette.text, fontWeight: 700, mb: 1 }}>
                  Private Event
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 1 }}>
                  {locationLabel || 'Location details are not publicly available.'}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textMuted, mb: 3 }}>
                  This event is not open for public registration.
                </Typography>
                <Button component={RouterLink} to="/tickets" variant="outlined" sx={outlinedButtonSx}>
                  Browse Other Events
                </Button>
              </Paper>
            ) : access.isFreeEntry ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: palette.success, fontWeight: 800, mb: 1 }}>
                  FREE ENTRY
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 3 }}>
                  No ticket purchase required.
                </Typography>
                <Button component={RouterLink} to="/tickets" variant="outlined" sx={outlinedButtonSx}>
                  Browse Other Events
                </Button>
              </Paper>
            ) : access.isExternalTickets ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: palette.text, fontWeight: 700, mb: 1 }}>
                  Tickets available from the organiser.
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 3 }}>
                  Tickets are sold on an external website. You will not be charged here.
                </Typography>
                <Button
                  variant="contained"
                  disabled={!access.externalTicketUrl}
                  endIcon={<OpenInNew sx={{ fontSize: 18 }} />}
                  onClick={() => openExternalTicketUrl(access.externalTicketUrl)}
                  sx={{ ...primaryButtonSx, px: 3, py: 1.25 }}
                >
                  Get Tickets
                </Button>
              </Paper>
            ) : soldOut ? (
              <Paper elevation={0} sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: palette.text, fontWeight: 700, mb: 1 }}>
                  This event is sold out
                </Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary, mb: 3 }}>
                  Tickets are no longer available for this event.
                </Typography>
                <Button component={RouterLink} to="/tickets" variant="outlined" sx={outlinedButtonSx}>
                  Browse Other Events
                </Button>
              </Paper>
            ) : (
              <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2.5, md: 3.5 } }}>
                {fewLeft && (
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      backgroundColor: 'rgba(255,167,38,0.1)',
                      color: palette.warning,
                      border: '1px solid rgba(255,167,38,0.3)',
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    {event.is_few_left_dummy
                      ? 'Few tickets left — book soon!'
                      : `Only ${event.tickets_remaining} ticket${event.tickets_remaining !== 1 ? 's' : ''} remaining — book soon!`}
                  </Alert>
                )}

                {/* Step 1 — details */}
                <StepLabel step={1} label="Your details" />
                <Grid container spacing={2} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={textFieldSx}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 3 }}>
                  <MailOutline sx={{ fontSize: 15, color: palette.textMuted }} />
                  <Typography variant="caption" sx={{ color: palette.textMuted }}>
                    Your e-ticket and QR code will be sent to this email
                  </Typography>
                </Box>

                {/* Step 2 — tickets */}
                <StepLabel step={2} label="Tickets" />
                <Box sx={{ mb: 2 }}>
                  {ticketTypes.map((type) => {
                    const typeSoldOut = type.is_sold_out;
                    const typeFewLeft = type.is_few_left;
                    const qty = quantities[type.id] || 0;
                    const maxQty = getMaxQtyForType(type);

                    return (
                      <Box
                        key={type.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          backgroundColor: palette.surfaceAlt,
                          border: `1px solid ${palette.border}`,
                          borderRadius: 2,
                          p: 2,
                          mb: 1.5,
                          opacity: typeSoldOut ? 0.6 : 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                            <Typography sx={{ color: palette.text, fontWeight: 700 }}>{type.name}</Typography>
                            {typeSoldOut && (
                              <Chip label="Sold Out" size="small" sx={{ height: 22, fontWeight: 700, backgroundColor: 'rgba(239,83,80,0.15)', color: palette.error }} />
                            )}
                            {!typeSoldOut && typeFewLeft && (
                              <Chip label={fewLeftLabel(type)} size="small" sx={{ height: 22, fontWeight: 700, backgroundColor: 'rgba(255,167,38,0.15)', color: palette.warning }} />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                            {formatPrice(type.price_pence)} each
                            {event?.platform_fee_enabled && event.platform_fee_type === 'per_ticket' && event.platform_fee_pence > 0
                              ? ` + ${formatPrice(event.platform_fee_pence)} platform fee`
                              : ''}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => changeTypeQuantity(type.id, -1)}
                            disabled={typeSoldOut || qty <= 0}
                            sx={{
                              border: `1px solid ${palette.borderStrong}`,
                              color: palette.text,
                              '&.Mui-disabled': { color: palette.textMuted, borderColor: palette.border },
                            }}
                          >
                            <Remove sx={{ fontSize: 18 }} />
                          </IconButton>
                          <Typography sx={{ color: palette.text, fontWeight: 800, fontSize: '1.2rem', minWidth: 28, textAlign: 'center' }}>
                            {qty}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => changeTypeQuantity(type.id, 1)}
                            disabled={typeSoldOut || qty >= maxQty || totalQuantity >= 10}
                            sx={{
                              border: `1px solid ${palette.accentBorder}`,
                              color: palette.accent,
                              '&.Mui-disabled': { color: palette.textMuted, borderColor: palette.border },
                            }}
                          >
                            <Add sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Coupon */}
                <Box sx={{ mb: 3 }}>
                  {couponApplied ? (
                    <Chip
                      icon={<CheckCircle sx={{ color: `${palette.success} !important` }} />}
                      label={`Coupon "${couponCode}" applied — you save ${formatPrice(savingsAmount)}`}
                      onDelete={handleRemoveCoupon}
                      deleteIcon={<Close sx={{ color: `${palette.success} !important` }} />}
                      sx={{
                        backgroundColor: palette.successSoft,
                        color: palette.success,
                        border: `1px solid ${palette.successBorder}`,
                        width: '100%',
                        justifyContent: 'space-between',
                        fontWeight: 600,
                        py: 2.25,
                      }}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="Discount code (optional)"
                      value={couponCode}
                      onChange={(e) => { setCouponCode(e.target.value); setCouponError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon(); }}
                      error={!!couponError}
                      helperText={couponError}
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalOffer sx={{ color: palette.textMuted, fontSize: 18 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={handleApplyCoupon}
                              disabled={couponLoading || !couponCode.trim()}
                              sx={{ color: palette.accent, textTransform: 'none', fontWeight: 700 }}
                            >
                              {couponLoading ? <CircularProgress size={16} sx={{ color: palette.accent }} /> : 'Apply'}
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                </Box>

                {/* Step 3 — payment / confirm */}
                <StepLabel step={3} label={isFreeOrder ? 'Confirm' : 'Payment'} />
                {isFreeOrder && (
                  <Alert
                    icon={<CheckCircle sx={{ color: `${palette.success} !important` }} />}
                    severity="success"
                    sx={{
                      mb: 1.5,
                      borderRadius: 2,
                      backgroundColor: palette.successSoft,
                      color: palette.success,
                      border: `1px solid ${palette.successBorder}`,
                      '& .MuiAlert-message': { fontWeight: 600 },
                    }}
                  >
                    No payment needed — your total is {formatPrice(0)}. Confirm to get your ticket.
                  </Alert>
                )}
                {applePayAvailable && !isFreeOrder && (
                  <>
                    <style>
                      {`
                        #apple-pay-button {
                          height: 48px;
                          width: 100%;
                          display: inline-block;
                          -webkit-appearance: -apple-pay-button;
                          -apple-pay-button-type: plain;
                          -apple-pay-button-style: white;
                          cursor: pointer;
                          border: 0;
                          padding: 0;
                          background-color: transparent;
                        }
                        #apple-pay-button:disabled {
                          opacity: 0.45;
                          cursor: not-allowed;
                        }
                      `}
                    </style>
                    <button
                      id="apple-pay-button"
                      type="button"
                      aria-label="Pay with Apple Pay"
                      disabled={loading || !purchaseItems.length}
                      onClick={handleApplePay}
                    />
                  </>
                )}
                <Box
                  id="google-pay-button"
                  onClick={handleGooglePay}
                  sx={{
                    display: googlePayAvailable && !isFreeOrder ? 'block' : 'none',
                    mt: applePayAvailable ? 1.5 : 0,
                    cursor: loading || !purchaseItems.length ? 'not-allowed' : 'pointer',
                    opacity: loading || !purchaseItems.length ? 0.45 : 1,
                    '& button': { width: '100%' },
                  }}
                />
                {(applePayAvailable || googlePayAvailable) && !isFreeOrder && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
                    <Divider sx={{ flex: 1, borderColor: palette.borderStrong }} />
                    <Typography variant="caption" sx={{ color: palette.textMuted, fontWeight: 700, letterSpacing: 0.6 }}>
                      or pay with card
                    </Typography>
                    <Divider sx={{ flex: 1, borderColor: palette.borderStrong }} />
                  </Box>
                )}
                <Box
                  id="card-container"
                  ref={cardContainerRef}
                  sx={{
                    display: isFreeOrder ? 'none' : 'block',
                    minHeight: 100,
                    borderRadius: 2,
                    backgroundColor: palette.surfaceAlt,
                    border: `1px solid ${palette.border}`,
                    p: 1.5,
                  }}
                />
                {!isFreeOrder && !cardReady && !cardInitError && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <CircularProgress size={16} sx={{ color: palette.accent }} />
                    <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                      Loading secure card form...
                    </Typography>
                  </Box>
                )}
                {!isFreeOrder && cardInitError && (
                  <Alert
                    severity="error"
                    sx={{ mt: 1.5, borderRadius: 2 }}
                    action={
                      <Button color="inherit" size="small" onClick={() => setCardInitKey((k) => k + 1)}>
                        Retry
                      </Button>
                    }
                  >
                    {cardInitError}
                  </Alert>
                )}

                {/* Mobile order summary */}
                <Box
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    mt: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: palette.surfaceAlt,
                    border: `1px dashed ${palette.borderStrong}`,
                  }}
                >
                  <SummaryRows {...summaryProps} />
                </Box>

                {error && (
                  <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>{error}</Alert>
                )}

                {/* <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    color: palette.textMuted,
                    fontWeight: 300,
                    fontSize: 12,
                    lineHeight: 1.7,
                    mt: 2.5,
                  }}
                >
                  By completing payment you agree to our{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setTermsOpen(true)}
                    sx={{
                      color: palette.textSecondary,
                      fontWeight: 400,
                      fontSize: 'inherit',
                      textDecoration: 'underline',
                      textDecorationColor: palette.borderStrong,
                      cursor: 'pointer',
                      verticalAlign: 'baseline',
                      '&:hover': { color: palette.accent },
                    }}
                  >
                    Terms &amp; Conditions
                  </Link>
                </Typography> */}

                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading || !purchaseItems.length || (!isFreeOrder && !cardReady)}
                  onClick={handlePurchase}
                  startIcon={!loading && (isFreeOrder ? <CheckCircle sx={{ fontSize: 18 }} /> : <Lock sx={{ fontSize: 18 }} />)}
                  sx={{ ...primaryButtonSx, mt: 3, py: 1.75, fontSize: '1.05rem' }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: palette.onAccent }} />
                  ) : isFreeOrder ? (
                    'Confirm ticket'
                  ) : (
                    `Pay ${formatPrice(total)}`
                  )}
                </Button>

                {!isFreeOrder && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 2 }}>
                    <CreditCard sx={{ fontSize: 15, color: palette.textMuted }} />
                    <Typography variant="caption" sx={{ color: palette.textMuted }}>
                      Payments secured &amp; encrypted by Square · Visa, Mastercard, Amex
                      {applePayAvailable ? ', Apple Pay' : ''}
                      {googlePayAvailable ? ', Google Pay' : ''}
                    </Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>

        <TicketTermsFooter sx={{ mt: 2 }} />
      </Box>

      <TicketTermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </Box>
  );
};

export default TicketPurchase;
